/**
 * Reference-audit report types (PR1 — pure referential integrity).
 *
 * These types describe the OUTPUT of `validateDrawingReferences` (and the
 * future `healDrawingReferences`, PR1b). They intentionally mirror the
 * ezdxf two-list audit model — `errors[]` (unresolved references) plus
 * `fixes[]` (auto-corrected substitutions, populated by heal only) — while
 * adding the structured context (`ownerKind` / `container` / `path` /
 * `refKind`) that the plan's Codex review #1 F16 / #2 P2-3 require so every
 * issue is locatable down to a nested array element without losing which
 * container (space OR block definition) owns it.
 *
 * The numeric `ReferenceErrorCode` values follow ezdxf's `AuditError`
 * IntEnum where a direct analogue exists (UNDEFINED_TEXT_STYLE=102,
 * UNDEFINED_BLOCK=103, INVALID_BLOCK_REFERENCE_CYCLE=104, …) so the codes
 * are recognizable to anyone familiar with the reference implementation.
 *
 * Source of truth: docs/superpowers/plans/2026-05-30-drawing-object-graph.md
 * §5.2 (types), §4 (46-field referential map), §11 + §14 (Codex logs).
 */

/**
 * Stable numeric error codes for every referential-integrity failure the
 * audit can emit. Values mirror ezdxf `AuditError` where an analogue
 * exists; the editor-specific codes (multileader style, annotation scale,
 * duplicate entity id) take adjacent free slots.
 */
export enum ReferenceErrorCode {
  UNDEFINED_LAYER = 105,
  UNDEFINED_TEXT_STYLE = 102,
  UNDEFINED_DIMENSION_STYLE = 101,
  UNDEFINED_MULTILEADER_STYLE = 106,
  UNDEFINED_BLOCK = 103,
  /** Enabled only after PR-LT2 (typed LTYPE parsing). Bypass-only in PR1. */
  UNDEFINED_LINETYPE = 100,
  UNDEFINED_ANNOTATION_SCALE = 107,
  INVALID_BLOCK_REFERENCE_CYCLE = 104,
  /** ownerHandle / ownerBlockRecordHandle target missing. */
  DANGLING_OWNER_HANDLE = 202,
  /** POINTER_TARGET_NOT_EXIST — layoutHandle / annotativeDecompositions value. */
  DANGLING_HANDLE = 3,
  /** mleader blockAttributeReferences[].attDefHandle target missing. */
  DANGLING_ATTDEF_HANDLE = 203,
  /** sourceEntityIds[] member missing from the owning container's id set. */
  DANGLING_ARRAY_SOURCE = 210,
  /** Two entities / block children share an id within one container. */
  DUPLICATE_ENTITY_ID = 211,
  /** Two symbol-table records share a (case-insensitive) name. */
  DUPLICATE_TABLE_ENTRY_NAME = 2,
  // DUPLICATE_HANDLE = 2 (cross-record) — DEFERRED to PR2: regular entities
  // carry NO `handle` (drawingEntityBaseSchema); handles live only on
  // blocks/blockRecords/layouts/attdef/layers. Coverage too sparse for MVP.

  // ── LTYPE internal-consistency (auditLinetypeFidelity, PR-LT2) ──
  // These are NOT cross-object references; they are emitted by the separate
  // report-only `auditLinetypeFidelity` pass (plan §10.2 / Codex#2 P2-7), not
  // by `validateDrawingReferences`. Numbered 220+ to stay clear of the ezdxf
  // reference (100-119) and entity (201-228) ranges used above.
  /** A reserved linetype (ByLayer/ByBlock/Continuous) carries pattern elements. */
  LINETYPE_RESERVED_HAS_ELEMENTS = 220,
  /** group-73 element count disagrees with elements.length. */
  LINETYPE_ELEMENT_COUNT_MISMATCH = 221,
  /** group-40 pattern length disagrees with Σ|element length| (derived only). */
  LINETYPE_PATTERN_LENGTH_MISMATCH = 222,
}

/** What kind of value a reference resolves against. */
export type RefKind = "name" | "handle" | "entityId";

/** What kind of record holds the offending reference. */
export type OwnerKind =
  | "entity"
  | "blockChild"
  | "styleTableRecord"
  | "blockDefinition"
  | "blockRecord"
  | "layout"
  | "document"
  | "linetype";

/**
 * One referential-integrity failure.
 *
 * Codex#1 F16: `ownerId`+`field` alone is too weak for nested arrays
 * (mleader leader lines, dynamic-block actions). We carry `ownerKind`,
 * `container`, a stable JSON `path`, and the `refKind` so a consumer can
 * locate AND rewrite the exact value.
 */
export interface ReferenceIssue {
  code: ReferenceErrorCode;
  ownerKind: OwnerKind;
  /** Referencing record id — entity.id, style/block/layout name, "document". */
  ownerId: string;
  /** Container that owns the entity: space id OR block-definition name (F5). */
  container?: string;
  /**
   * Stable JSON path to the offending value, e.g. "styleOverrides.dimblk",
   * "blockAttributeReferences[0].attDefHandle",
   * "annotativeDecompositions['1:50']".
   */
  path: string;
  refKind: RefKind;
  field: string;
  referencedKey: string;
  message: string;
}

/**
 * A heal substitution (PR1b). A fix is NOT a severity on an error (Codex#1
 * F16): `healDrawingReferences` emits one `ReferenceFix` for every dangling
 * reference it auto-corrects.
 *
 * The record mirrors `ReferenceIssue`'s locational shape (`ownerKind` /
 * `ownerId` / `container` / `path` / `field` / `refKind` / `code`) so a
 * consumer or AUDIT-style transcript can render an applied fix and an
 * unresolved error UNIFORMLY (the same columns), and adds the substitution
 * pair (`from` → `to`) plus a human `message`. AutoCAD's AUDIT/RECOVER and
 * ezdxf's `Auditor.fixed_errors` use exactly this "what was changed, from
 * what, to what" log shape.
 */
export interface ReferenceFix {
  code: ReferenceErrorCode;
  ownerKind: OwnerKind;
  /** Referencing record id — entity.id, style/block/layout name, "document". */
  ownerId: string;
  /** Container that owns the entity: space id OR block-definition name (F5). */
  container?: string;
  /** Stable JSON path to the healed value (mirrors `ReferenceIssue.path`). */
  path: string;
  refKind: RefKind;
  field: string;
  /** Original (dangling / empty) value before the substitution. */
  from: string;
  /** Substituted always-present default value after the heal. */
  to: string;
  message: string;
}

/** Result of an audit / heal pass. `ok === (errors.length === 0)`. */
export interface ReferenceAuditReport {
  /** ok === (errors.length === 0) */
  errors: ReferenceIssue[];
  /** Populated by healDrawingReferences only; [] from validateDrawingReferences. */
  fixes: ReferenceFix[];
  ok: boolean;
}

/**
 * The complete, compiler-enforced context every emitter must supply
 * (Codex#2 P2-3 — no `Partial<…> as …` casts). `code` / `refKind` /
 * `referencedKey` / `message` are added by the emit helper.
 */
export type IssueContext = Pick<
  ReferenceIssue,
  "ownerKind" | "ownerId" | "container" | "path" | "field"
>;
